import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import { LuPalette, LuSave, LuRefreshCw, LuCheck, LuSun, LuMoon, LuEye } from 'react-icons/lu';

// Преобразование HEX в HSL
const hexToHSL = (hex) => {
  if (!hex) return { h: 0, s: 0, l: 50 };
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

// Преобразование HSL в HEX
const hslToHex = (h, s, l) => {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

// Компактный Color Picker
const ColorPicker = ({ color, onChange, label }) => {
  const [hsl, setHsl] = useState(() => hexToHSL(color));
  const [isDragging, setIsDragging] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const satLightRef = useRef(null);
  const hueRef = useRef(null);

  useEffect(() => {
    setHsl(hexToHSL(color));
  }, [color]);

  const updateColor = useCallback((newHsl) => {
    setHsl(newHsl);
    onChange(hslToHex(newHsl.h, newHsl.s, newHsl.l));
  }, [onChange]);

  const handleSatLightMove = useCallback((e) => {
    if (!satLightRef.current) return;
    const rect = satLightRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    updateColor({ ...hsl, s: Math.round(x * 100), l: Math.round((1 - y) * 50 + (1 - x) * 50 * (1 - y)) });
  }, [hsl, updateColor]);

  const handleHueMove = useCallback((e) => {
    if (!hueRef.current) return;
    const rect = hueRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    updateColor({ ...hsl, h: Math.round(x * 360) });
  }, [hsl, updateColor]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging === 'satlight') handleSatLightMove(e);
      else if (isDragging === 'hue') handleHueMove(e);
    };
    const handleMouseUp = () => setIsDragging(null);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleSatLightMove, handleHueMove]);

  return (
    <div className="color-picker-compact">
      <div className="color-picker-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="color-preview-small" style={{ background: color }} />
        <span className="color-picker-label">{label}</span>
        <input 
          type="text" 
          value={color?.toUpperCase() || '#000000'} 
          onChange={(e) => {
            e.stopPropagation();
            const val = e.target.value;
            if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
              onChange(val);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          className="color-hex-input-small"
          maxLength={7}
        />
      </div>
      
      {isExpanded && (
        <div className="color-picker-expanded">
          <div 
            ref={satLightRef}
            className="color-picker-satlight-small"
            style={{ background: `linear-gradient(to right, #fff, hsl(${hsl.h}, 100%, 50%))` }}
            onMouseDown={(e) => { setIsDragging('satlight'); handleSatLightMove(e); }}
          >
            <div className="satlight-overlay" />
            <div 
              className="color-picker-cursor-small"
              style={{ 
                left: `${hsl.s}%`, 
                top: `${100 - hsl.l}%`,
                background: color
              }}
            />
          </div>
          
          <div 
            ref={hueRef}
            className="color-picker-hue-small"
            onMouseDown={(e) => { setIsDragging('hue'); handleHueMove(e); }}
          >
            <div 
              className="hue-cursor-small"
              style={{ left: `${(hsl.h / 360) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Значения по умолчанию
const defaultTheme = {
  light: {
    primary: '#3f69ff',
    bgPrimary: '#f8fafc',
    bgSecondary: '#eef2ff',
    textPrimary: '#0f172a',
    textSecondary: '#64748b'
  },
  dark: {
    primary: '#6c7bff',
    bgPrimary: '#050816',
    bgSecondary: '#0f172a',
    textPrimary: '#e2e8f0',
    textSecondary: '#94a3b8'
  }
};

// Пресеты цветовых схем
const colorPresets = [
  { 
    name: 'Синий (по умолчанию)', 
    light: { primary: '#3f69ff', bgPrimary: '#f8fafc', bgSecondary: '#eef2ff', textPrimary: '#0f172a', textSecondary: '#64748b' },
    dark: { primary: '#6c7bff', bgPrimary: '#050816', bgSecondary: '#0f172a', textPrimary: '#e2e8f0', textSecondary: '#94a3b8' }
  },
  { 
    name: 'Фиолетовый', 
    light: { primary: '#8b5cf6', bgPrimary: '#faf5ff', bgSecondary: '#f3e8ff', textPrimary: '#1e1b4b', textSecondary: '#6b7280' },
    dark: { primary: '#a78bfa', bgPrimary: '#0c0a1d', bgSecondary: '#1e1b4b', textPrimary: '#e9d5ff', textSecondary: '#a5b4fc' }
  },
  { 
    name: 'Изумрудный', 
    light: { primary: '#10b981', bgPrimary: '#f0fdf4', bgSecondary: '#dcfce7', textPrimary: '#064e3b', textSecondary: '#6b7280' },
    dark: { primary: '#34d399', bgPrimary: '#022c22', bgSecondary: '#064e3b', textPrimary: '#d1fae5', textSecondary: '#86efac' }
  },
  { 
    name: 'Розовый', 
    light: { primary: '#ec4899', bgPrimary: '#fdf2f8', bgSecondary: '#fce7f3', textPrimary: '#831843', textSecondary: '#6b7280' },
    dark: { primary: '#f472b6', bgPrimary: '#1a0a14', bgSecondary: '#831843', textPrimary: '#fbcfe8', textSecondary: '#f9a8d4' }
  },
  { 
    name: 'Оранжевый', 
    light: { primary: '#f97316', bgPrimary: '#fffbeb', bgSecondary: '#fef3c7', textPrimary: '#7c2d12', textSecondary: '#6b7280' },
    dark: { primary: '#fb923c', bgPrimary: '#1c1108', bgSecondary: '#7c2d12', textPrimary: '#fed7aa', textSecondary: '#fdba74' }
  },
  { 
    name: 'Тёмный минимализм', 
    light: { primary: '#374151', bgPrimary: '#f9fafb', bgSecondary: '#f3f4f6', textPrimary: '#111827', textSecondary: '#6b7280' },
    dark: { primary: '#9ca3af', bgPrimary: '#030712', bgSecondary: '#111827', textPrimary: '#f9fafb', textSecondary: '#d1d5db' }
  },
];

export default function AdminThemeSettingsPage() {
  const { token } = useAuth();
  const { theme: currentTheme, updateThemeColors } = useTheme();
  
  // Состояние для светлой темы
  const [lightColors, setLightColors] = useState(defaultTheme.light);
  // Состояние для тёмной темы
  const [darkColors, setDarkColors] = useState(defaultTheme.dark);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [previewTheme, setPreviewTheme] = useState(currentTheme);

  // Загрузка настроек
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch('/api/admin/system-settings', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setLightColors({
            primary: data.theme_primary_color || defaultTheme.light.primary,
            bgPrimary: data.theme_bg_primary || defaultTheme.light.bgPrimary,
            bgSecondary: data.theme_bg_secondary || defaultTheme.light.bgSecondary,
            textPrimary: data.theme_text_primary || defaultTheme.light.textPrimary,
            textSecondary: data.theme_text_secondary || defaultTheme.light.textSecondary
          });
          setDarkColors({
            primary: data.theme_primary_color_dark || defaultTheme.dark.primary,
            bgPrimary: data.theme_bg_primary_dark || defaultTheme.dark.bgPrimary,
            bgSecondary: data.theme_bg_secondary_dark || defaultTheme.dark.bgSecondary,
            textPrimary: data.theme_text_primary_dark || defaultTheme.dark.textPrimary,
            textSecondary: data.theme_text_secondary_dark || defaultTheme.dark.textSecondary
          });
        }
      } catch (e) {
        console.error('Ошибка загрузки настроек темы:', e);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchSettings();
  }, [token]);

  // Применение цветов в реальном времени для превью
  useEffect(() => {
    const root = document.documentElement;
    const colors = previewTheme === 'light' ? lightColors : darkColors;
    
    root.style.setProperty('--accent-primary', colors.primary);
    root.style.setProperty('--bg-primary', colors.bgPrimary);
    root.style.setProperty('--bg-secondary', colors.bgSecondary);
    root.style.setProperty('--text-primary', colors.textPrimary);
    root.style.setProperty('--text-secondary', colors.textSecondary);
    root.style.setProperty('--body-bg', previewTheme === 'light' 
      ? `linear-gradient(180deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 35%, ${colors.bgSecondary} 100%)`
      : `radial-gradient(circle at 20% 20%, ${colors.primary}40, transparent 45%), ${colors.bgPrimary}`
    );
    root.style.setProperty('--glass-bg', previewTheme === 'light' 
      ? `${colors.bgPrimary}f2`
      : `${colors.bgSecondary}eb`
    );
    root.style.setProperty('--surface-strong', `${colors.primary}20`);
  }, [lightColors, darkColors, previewTheme]);

  // Сохранение
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/admin/system-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          theme_primary_color: lightColors.primary,
          theme_bg_primary: lightColors.bgPrimary,
          theme_bg_secondary: lightColors.bgSecondary,
          theme_text_primary: lightColors.textPrimary,
          theme_text_secondary: lightColors.textSecondary,
          theme_primary_color_dark: darkColors.primary,
          theme_bg_primary_dark: darkColors.bgPrimary,
          theme_bg_secondary_dark: darkColors.bgSecondary,
          theme_text_primary_dark: darkColors.textPrimary,
          theme_text_secondary_dark: darkColors.textSecondary
        })
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        updateThemeColors(lightColors.primary, darkColors.primary);
      }
    } catch (e) {
      alert('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  // Сброс к значениям по умолчанию
  const handleReset = () => {
    setLightColors(defaultTheme.light);
    setDarkColors(defaultTheme.dark);
  };

  // Применение пресета
  const applyPreset = (preset) => {
    setLightColors(preset.light);
    setDarkColors(preset.dark);
  };

  // Обновление цвета
  const updateLightColor = (key, value) => {
    setLightColors(prev => ({ ...prev, [key]: value }));
  };
  
  const updateDarkColor = (key, value) => {
    setDarkColors(prev => ({ ...prev, [key]: value }));
  };

  if (loading) return <div className="loading-mini">Загрузка...</div>;

  const currentColors = previewTheme === 'light' ? lightColors : darkColors;
  const setCurrentColors = previewTheme === 'light' ? updateLightColor : updateDarkColor;

  return (
    <main className="admin-page-content">
      <section className="glass-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <LuPalette size={28} style={{ color: 'var(--accent-primary)' }} />
          <h2 style={{ margin: 0 }}>Полная настройка темы</h2>
        </div>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
          Настройте цветовую схему панели: фон, акцентные цвета и текст. Изменения применяются ко всему интерфейсу.
        </p>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px' }}>
        {/* Левая колонка - Редактор цветов */}
        <section className="glass-card">
          {/* Переключатель темы для редактирования */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
            <button 
              className={`btn ${previewTheme === 'light' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPreviewTheme('light')}
              style={{ flex: 1 }}
            >
              <LuSun /> Светлая
            </button>
            <button 
              className={`btn ${previewTheme === 'dark' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPreviewTheme('dark')}
              style={{ flex: 1 }}
            >
              <LuMoon /> Тёмная
            </button>
          </div>

          <h4 style={{ margin: '0 0 16px', color: 'var(--text-secondary)' }}>
            {previewTheme === 'light' ? '☀️ Настройки светлой темы' : '🌙 Настройки тёмной темы'}
          </h4>

          <div className="color-settings-grid">
            <ColorPicker 
              color={currentColors.primary} 
              onChange={(c) => setCurrentColors('primary', c)}
              label="Акцентный цвет"
            />
            <ColorPicker 
              color={currentColors.bgPrimary} 
              onChange={(c) => setCurrentColors('bgPrimary', c)}
              label="Основной фон"
            />
            <ColorPicker 
              color={currentColors.bgSecondary} 
              onChange={(c) => setCurrentColors('bgSecondary', c)}
              label="Вторичный фон"
            />
            <ColorPicker 
              color={currentColors.textPrimary} 
              onChange={(c) => setCurrentColors('textPrimary', c)}
              label="Основной текст"
            />
            <ColorPicker 
              color={currentColors.textSecondary} 
              onChange={(c) => setCurrentColors('textSecondary', c)}
              label="Вторичный текст"
            />
          </div>

          {/* Кнопки действий */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={saving}
              style={{ flex: 1 }}
            >
              {saving ? 'Сохранение...' : saved ? <><LuCheck /> Сохранено</> : <><LuSave /> Сохранить</>}
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleReset}
              title="Сбросить к значениям по умолчанию"
            >
              <LuRefreshCw />
            </button>
          </div>
        </section>

        {/* Средняя колонка - Пресеты */}
        <section className="glass-card">
          <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Готовые схемы</h3>
          
          <div className="theme-presets-grid">
            {colorPresets.map((preset, idx) => (
              <button
                key={idx}
                className={`theme-preset-btn ${
                  lightColors.primary === preset.light.primary && 
                  darkColors.primary === preset.dark.primary ? 'active' : ''
                }`}
                onClick={() => applyPreset(preset)}
                title={preset.name}
              >
                <div className="preset-colors-row">
                  <span style={{ background: preset.light.bgPrimary, border: '1px solid #ddd' }} />
                  <span style={{ background: preset.light.primary }} />
                  <span style={{ background: preset.dark.bgPrimary }} />
                  <span style={{ background: preset.dark.primary }} />
                </div>
                <span className="preset-name">{preset.name}</span>
                {lightColors.primary === preset.light.primary && 
                 darkColors.primary === preset.dark.primary && (
                  <LuCheck className="preset-check" />
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Правая колонка - Превью */}
        <section className="glass-card">
          <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
            <LuEye style={{ marginRight: '8px' }} />
            Превью
          </h3>
          
          <div 
            className="theme-preview-box"
            style={{ 
              background: currentColors.bgPrimary,
              color: currentColors.textPrimary,
              borderRadius: '16px',
              padding: '20px',
              border: `1px solid ${currentColors.textSecondary}30`
            }}
          >
            <div style={{ 
              background: currentColors.bgSecondary, 
              borderRadius: '12px', 
              padding: '16px',
              marginBottom: '16px'
            }}>
              <h4 style={{ margin: '0 0 8px', color: currentColors.textPrimary }}>Карточка</h4>
              <p style={{ margin: 0, color: currentColors.textSecondary, fontSize: '14px' }}>
                Вторичный текст внутри карточки
              </p>
            </div>
            
            <button style={{ 
              background: currentColors.primary,
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '10px',
              cursor: 'pointer',
              width: '100%',
              fontWeight: 600,
              marginBottom: '12px'
            }}>
              Кнопка
            </button>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <span style={{ 
                background: `${currentColors.primary}20`,
                color: currentColors.primary,
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 600
              }}>Тег 1</span>
              <span style={{ 
                background: `${currentColors.primary}20`,
                color: currentColors.primary,
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 600
              }}>Тег 2</span>
            </div>
            
            <a 
              href="#preview" 
              onClick={(e) => e.preventDefault()}
              style={{ color: currentColors.primary, textDecoration: 'none' }}
            >
              Ссылка с акцентным цветом →
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}
